import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { Button } from 'devextreme-react'
import classes from './References.module.scss'
import { PulseLoader } from 'react-spinners'
import { readProperty } from '@/routes/utils'

export interface GenericReferencesProps {
  /**
   * The query to use to fetch the children.
   */
  query: string
  /**
   * Callback when data is loaded. Receives the count of items.
   */
  onDataLoaded?: (count: number) => void

  /**
   * The field in the child that we'll use to filter the children.
   */
  dataField: string

  /**
   * The field in the child that we'll display, e.g. "ded_Dis".
   */
  displayField: string

  /**
   * The field in the child that we'll use to get the id.
   */
  keyField: string

  /**
   * If we want a clickable link, we open /entityRoute/{childId}. Omit if no links.
   */
  entityRoute?: string

  /**
   * How many references to show before "Show more..."
   */
  maxCount?: number
}

export function ReferencesV2({
  displayField,
  keyField,
  query,
  onDataLoaded,
  entityRoute,
  dataField,
  maxCount = 4,
}: GenericReferencesProps) {
  const { service, client } = useQsAdminApiManager()
  const [showAll, setShowAll] = useState(false)

  // We expect data.value = array of parent objects. Each parent has parent[childCollectionName] = array of children.
  const { data, isLoading, error } = useQuery({
    queryKey: ['referencesV2', query],
    queryFn: async () => {
      console.log('query', query)
      try {
        const response = await client.get(query)

        // Handle cases where the response is empty or missing the expected field
        if (!response || !response.data) {
          console.warn('Empty response', response)
          return []
        }

        if (!response.data.value || !Array.isArray(response.data.value) || response.data.value.length === 0) {
          console.warn('Response missing value array or empty array', response.data)
          return []
        }

        // Safely access nested property using our improved readProperty function
        const result = readProperty(response.data.value[0], dataField, [])

        // Ensure we always return an array
        return Array.isArray(result) ? result : []
      } catch (error) {
        console.error('Error fetching data:', error)
        return [] // Return empty array to prevent undefined errors
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })

  const finalData = data || []

  useEffect(() => {
    // When loading is finished, report the count.
    if (!isLoading) {
      onDataLoaded?.(finalData.length)
    }
  }, [finalData.length, isLoading, onDataLoaded])

  if (isLoading) return <PulseLoader size={10} />
  if (error) return <>Error: {String(error)}</>
  if (!data || !Array.isArray(data)) return <>No data available</>

  // Flatten out the children
  // const parents = Array.isArray(data) ? data : []
  // const allChildren: any[] = []
  // for (const p of parents) {
  //   const childArray = p?.[childCollectionName]
  //   if (Array.isArray(childArray)) {
  //     allChildren.push(...childArray)
  //   }
  // }

  const displayed = showAll ? data : data.slice(0, maxCount)
  const remainingCount = Math.max(data.length - maxCount, 0)

  const handleClick = (childId: number) => {
    if (!entityRoute) return
    window.open(`/${entityRoute}/${childId}`, '_blank')
  }

  return (
    <div className={classes.labelContainer}>
      {displayed.map((item: any, index: number) => {
        // Use improved readProperty to safely access nested properties
        // Pass a default value for explicitly undefined cases
        const itemKey = readProperty(item, keyField, null)

        // Skip rendering this item if key is not available
        if (itemKey === null) {
          console.log('Missing or invalid item key:', { item, keyField })
          return null
        }

        // Get the display label, defaulting to the key if not available
        const label = String(readProperty(item, displayField, itemKey) || '')

        return (
          <Button key={index} onClick={() => handleClick(itemKey)} className={classes.label}>
            {label}
          </Button>
        )
      })}
      {!showAll && data.length > maxCount && (
        <Button className={classes.toggleExpand} onClick={() => setShowAll(true)}>
          Show {remainingCount} more...
        </Button>
      )}
      {showAll && data.length > maxCount && (
        <Button className={classes.toggleExpand} onClick={() => setShowAll(false)}>
          Show less
        </Button>
      )}
    </div>
  )
}
